import { createSelector } from 'reselect'
import { selectCurrentUser } from 'ducks/currentUser/selectors'
import {
  isClosed,
  isDeleted,
  isSpam,
  isStarred,
  isUnread,
  getStateLabel,
  getInvertStateActionLabel,
  isOpen,
  isCloseable,
  isSnoozed,
} from 'util/ticketState'
import { selectCurrentTicketUndecorated as selectUndecorated } from './current'
import { makeSelectTicketById, makeSelectRawTicketById } from './byId'

export const selectCurrentTicketIsUnread = createSelector(
  selectUndecorated,
  isUnread
)

export const selectCurrentTicketIsClosed = createSelector(
  selectUndecorated,
  isClosed
)

export const selectCurrentTicketIsSpam = createSelector(
  selectUndecorated,
  isSpam
)

export const selectCurrentTicketIsDeleted = createSelector(
  selectUndecorated,
  isDeleted
)

export const selectCurrentTicketIsFollowing = createSelector(
  selectUndecorated,
  selectCurrentUser,
  ({ follower_ids: followerIds }, { id }) => {
    return followerIds && followerIds.includes(id)
  }
)

export const selectCurrentTicketSnoozedUntil = createSelector(
  selectUndecorated,
  ticket => ticket.snoozedUntil
)

export const selectCurrentTicketIsSnoozed = createSelector(
  selectUndecorated,
  ticket => isSnoozed(ticket)
)

export const selectCurrentTicketIsStarred = createSelector(
  selectUndecorated,
  isStarred
)

export const selectInversedStarredPriority = createSelector(
  selectCurrentTicketIsStarred,
  starred => (starred ? 'low' : 'urgent')
)

export const selectInversedStarredLabel = createSelector(
  selectCurrentTicketIsStarred,
  starred => (starred ? 'unstarred' : 'starred')
)

export const selectCurrentTicketStateLabel = createSelector(
  selectUndecorated,
  ticket => getStateLabel(ticket.state, ticket.snoozedUntil, ticket.deleted_at)
)

export function makeSelectTicketFlags() {
  const selectTicketById = makeSelectRawTicketById()
  return createSelector(selectTicketById, ticket => {
    return {
      isOpen: isOpen(ticket),
      isClosed: isClosed(ticket),
      isSpam: isSpam(ticket),
      isUnread: isUnread(ticket),
      isStarred: isStarred(ticket),
      isDeleted: isDeleted(ticket),
      isCloseable: isCloseable(ticket),
      isSnoozed: isSnoozed(ticket),
    }
  })
}

export function makeSelectTicketSnoozedUntil() {
  const selectTicketById = makeSelectTicketById()
  return createSelector(selectTicketById, ticket => ticket.snoozedUntil)
}

export function makeSelectTicketStateLabel() {
  const selectTicketById = makeSelectTicketById()
  return createSelector(selectTicketById, ticket =>
    getStateLabel(ticket.state, ticket.snoozedUntil, ticket.deleted_at)
  )
}

export function makeSelectTicketInvertStateActionLabel() {
  const selectTicketById = makeSelectTicketById()
  return createSelector(selectTicketById, ticket =>
    getInvertStateActionLabel(
      ticket.state,
      ticket.snoozedUntil,
      ticket.deleted_at
    )
  )
}

export function makeSelectTicketIsUnread() {
  const selectTicketById = makeSelectTicketById()
  return createSelector(selectTicketById, ticket => isUnread(ticket))
}
